import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiClientError, Auth0LoginButton, useAuth } from 'grabbitbot-legacy-ui'
import { AUTH0_LOGIN_EXPECTED_ORIGIN, AUTH0_LOGIN_URL, POST_LOGIN_PATH } from '../../config'
import { describeError } from '../../api/errorMessages'

// Equivalent of legacy `UserPasswordForgotComponent` (audit §6.3).
// 2026-09-26: password reset moved to Auth0's own "Forgot password?" link.
// 0.15.0 (ticket 824c05cf): imported legacy users have NO Auth0 account, so
// that link mails them nothing — this page asks the backend
// (`POST /user/password/forgot`) to send them a reset link, which works
// whether or not they ever signed in through Auth0.
//
// The confirmation is deliberately the SAME for every address and every
// success: the page must never reveal whether an account exists. The Auth0
// sign-in button stays as the secondary route.

const NEUTRAL_CONFIRMATION =
  'If an account exists for this address, we sent a link to reset the password. The link works for one hour; check your spam folder if it does not arrive.'

export function PasswordForgotPage() {
  const navigate = useNavigate()
  const { requestPasswordReset } = useAuth()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const address = email.trim()
    if (address.length === 0) {
      setError('Please enter your e-mail address.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await requestPasswordReset(address)
      setSent(true)
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 410) {
        // The backend's switch is still off: the old Auth0-only way.
        setError(
          'Reset links by e-mail are not available yet. Click "Log in" below, then use the "Forgot password?" link on the sign-in screen.',
        )
      } else if (err instanceof ApiClientError && err.status === 429) {
        setError('Too many requests from your network. Please wait a while and try again.')
      } else {
        setError(describeError(err, 'Could not send the reset link'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="portal-card portal-card--auth">
      <h1>Reset your password</h1>
      {sent ? (
        <p role="status">{NEUTRAL_CONFIRMATION}</p>
      ) : (
        <form className="portal-form" onSubmit={handleSubmit} noValidate>
          <label>
            E-mail
            <input
              type="email"
              name="username"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {error && <p role="alert">{error}</p>}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
      <p>
        Used GrabbitBot before the move to the new sign-in? Enter the e-mail address of your old account here — you
        will get a link to choose a password, even if you never signed in on the new system.
      </p>
      <p>Or sign in with your existing password or Google:</p>
      <p>
        <Auth0LoginButton
          popupUrl={AUTH0_LOGIN_URL}
          expectedOrigin={AUTH0_LOGIN_EXPECTED_ORIGIN}
          onSuccess={() => navigate(POST_LOGIN_PATH, { replace: true })}
        >
          Log in
        </Auth0LoginButton>
      </p>
      <p>
        <Link to="/login">Back to sign in</Link>
      </p>
    </div>
  )
}
